import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePrincipal } from '@/hooks/usePrincipal';
import type { RouteHandle } from '@/routes/types';
import type { ReactNode } from 'react';
import { useMatches } from 'react-router-dom';

interface RequirePermissionsProps {
  children: ReactNode;
}

/**
 * Route-level permission gate. Reads the deepest matched route's
 * `handle.requiredPermissions` via `useMatches()` and renders a 403-style
 * "missing permission" card when the authed caller lacks every one of
 * them; otherwise renders children.
 *
 * Auth check has moved up to `AuthedLayout` — every page that mounts under
 * this component is already past the auth gate. `[]` (the default for any
 * route omitting `handle.requiredPermissions`) means "no permission check
 * beyond authentication".
 *
 * Permissions are a SINGLE source of truth on the route handle. Sidebar
 * nav-config, command palette, and this gate all read from the same field;
 * never maintain a parallel list elsewhere.
 */
export function RequirePermissions({ children }: RequirePermissionsProps) {
  const principal = usePrincipal();
  const required = useRequiredPermissions();

  // `AuthedLayout` is the outer gate — by the time we mount, the principal
  // is either authenticated (the expected case) or transiently loading /
  // erroring while the cache resolves. Loading paints nothing here (the
  // outer LoadingScreen owns that UX); error falls through to the missing-
  // permission card (fail-closed). Anonymous is impossible under the
  // outer gate but we treat it as fail-closed too for type completeness.
  if (principal.status !== 'authenticated') {
    if (required.length === 0) return <>{children}</>;
    return <MissingPermissionCard required={required} />;
  }

  if (required.length > 0 && !principal.hasAnyPermission(required)) {
    return <MissingPermissionCard required={required} />;
  }

  return <>{children}</>;
}

function MissingPermissionCard({ required }: { required: readonly string[] }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-6">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Missing permission</CardTitle>
          <CardDescription>
            Your account doesn&apos;t have access to this page. If you believe this is a mistake,
            contact a marketplace administrator.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">Required: {required.join(', ')}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Walks the matched route chain (root → leaf) and returns the deepest
 * `handle.requiredPermissions` declared. Returning the deepest entry means
 * a nested route can tighten its parent's gate without the parent having
 * to know which children exist.
 */
function useRequiredPermissions(): readonly string[] {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    if (match === undefined) continue;
    const handle = match.handle as RouteHandle | null | undefined;
    if (handle?.requiredPermissions !== undefined) {
      return handle.requiredPermissions;
    }
  }
  return [];
}
